import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BLOCK_TYPES = [
  { value: 'richtext', label: 'Rich Text' },
  { value: 'button', label: 'Button' },
  { value: 'image', label: 'Image' },
];

const defaultBlock = { type: 'richtext', value: '', align: 'left' };

const EmailComposer = ({ 
  emailFields, 
  onEmailFieldsChange, 
  bodyBlocks, 
  onBodyBlocksChange, 
  showAttributeInserter = true,
  showProgrammedToggles = false,
  readOnly = false,
  allowTestInput = false,
  children 
}) => {
  // Attribute inserter state
  const [showAttrDialog, setShowAttrDialog] = useState(false);
  const [attrName, setAttrName] = useState('');
  const [focusTarget, setFocusTarget] = useState({ type: null, index: null, subfield: null });
  const inputRefs = useRef({}); // {subject, previewText, block-0-richtext, block-0-button-text, ...}
  const quillRefs = useRef({}); // {block-0-richtext: ref, ...}

  // Helper to update repeater fields
  const handleRepeaterChange = (field, idx, value) => {
    onEmailFieldsChange(prev => {
      const arr = [...prev[field]];
      arr[idx] = value;
      return { ...prev, [field]: arr };
    });
  };
  const addRepeaterField = (field) => {
    onEmailFieldsChange(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };
  const removeRepeaterField = (field, idx) => {
    onEmailFieldsChange(prev => {
      const arr = [...prev[field]];
      arr.splice(idx, 1);
      return { ...prev, [field]: arr };
    });
  };

  // Body block helpers
  const addBlock = (type = 'richtext') => {
    onBodyBlocksChange(blocks => [...blocks, { type, value: type === 'richtext' ? '' : {}, align: 'left' }]);
  };
  const removeBlock = (idx) => {
    onBodyBlocksChange(blocks => blocks.length > 1 ? blocks.filter((_, i) => i !== idx) : blocks);
  };
  const moveBlock = (idx, dir) => {
    onBodyBlocksChange(blocks => {
      const arr = [...blocks];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };
  const updateBlock = (idx, newBlock) => {
    onBodyBlocksChange(blocks => blocks.map((b, i) => (i === idx ? newBlock : b)));
  };

  // Compose HTML from blocks
  const buildBodyHtml = () => {
    return bodyBlocks.map((block, idx) => {
      const align = block.align || 'left';
      let alignStyle = '';
      if (align === 'center') alignStyle = 'text-align:center;';
      if (align === 'right') alignStyle = 'text-align:right;';
      switch (block.type) {
        case 'richtext':
          return `<div style="${alignStyle}">${block.value || ''}</div>`;
        case 'button':
          if (!block.value.text || !block.value.link) return '';
          let btnWrap = align === 'center' ? 'margin:16px auto;text-align:center;display:block;' : align === 'right' ? 'margin:16px 0 16px auto;text-align:right;display:block;' : 'margin:16px 0;text-align:left;display:block;';
          return `<div style="${btnWrap}"><a href="${block.value.link}" style="background:#2563eb;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block;font-weight:600;">${block.value.text}</a></div>`;
        case 'image':
          if (!block.value.src) return '';
          const w = block.value.width ? `width:${block.value.width}px;` : '';
          const h = block.value.height ? `height:${block.value.height}px;` : '';
          let imgWrap = align === 'center' ? 'text-align:center;' : align === 'right' ? 'text-align:right;' : 'text-align:left;';
          return `<div style="margin:16px 0;${imgWrap}"><img src="${block.value.src}" style="max-width:100%;${w}${h}" alt="Image" /></div>`;
        default:
          return '';
      }
    }).join('');
  };

  // Attribute inserter logic
  const openAttrDialog = () => setShowAttrDialog(true);
  const closeAttrDialog = () => {
    setShowAttrDialog(false);
    setAttrName('');
  };
  const handleAttrInsert = () => {
    if (!attrName) return;
    const insertText = ` ${'${' + attrName + '}'} `.replace('\u001b', ''); // ensure no accidental escapes
    // Focus target: { type: 'subject' | 'previewText' | 'block', index, subfield }
    if (focusTarget.type === 'subject' || focusTarget.type === 'previewText') {
      const key = focusTarget.type;
      const ref = inputRefs.current[key];
      if (ref) {
        const el = ref;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const value = emailFields[key];
        const newValue = value.slice(0, start) + insertText + value.slice(end);
        onEmailFieldsChange(prev => ({ ...prev, [key]: newValue }));
        setTimeout(() => {
          el.focus();
          el.setSelectionRange(start + insertText.length, start + insertText.length);
        }, 0);
      }
    } else if (focusTarget.type === 'block') {
      const idx = focusTarget.index;
      const block = bodyBlocks[idx];
      if (block.type === 'richtext') {
        // Insert into ReactQuill
        const quillRef = quillRefs.current[`block-${idx}-richtext`];
        if (quillRef && quillRef.getEditor) {
          const editor = quillRef.getEditor();
          const range = editor.getSelection(true);
          if (range) {
            editor.insertText(range.index, insertText);
          } else {
            editor.insertText(0, insertText);
          }
        }
      } else if (block.type === 'button' || block.type === 'image') {
        // Insert into plain input
        const subfield = focusTarget.subfield;
        const ref = inputRefs.current[`block-${idx}-${block.type}-${subfield}`];
        if (ref) {
          const el = ref;
          const start = el.selectionStart;
          const end = el.selectionEnd;
          const value = block.value[subfield] || '';
          const newValue = value.slice(0, start) + insertText + value.slice(end);
          const newBlock = {
            ...block,
            value: {
              ...block.value,
              [subfield]: newValue
            }
          };
          updateBlock(idx, newBlock);
          setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + insertText.length, start + insertText.length);
          }, 0);
        }
      }
    }
    closeAttrDialog();
  };

  // Helper for chip-style input
  function EmailChipsInput({ label, value, onChange, placeholder, icon, helper, fieldName, programmedValue, onProgrammedChange, readOnly = false, allowTestInput = false }) {
    const [input, setInput] = useState('');
    const addChip = () => {
      const email = input.trim();
      if (email && !value.includes(email)) {
        onChange([...value, email]);
        setInput('');
      }
    };
    const removeChip = idx => {
      onChange(value.filter((_, i) => i !== idx));
    };
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 15, color: '#374151' }}>
            <span style={{ fontSize: 20 }}>{icon}</span> {label}
            {helper && <span style={{ fontSize: 12, color: '#888', marginLeft: 6, cursor: 'help' }} title={helper}>?</span>}
          </label>
          {showProgrammedToggles && onProgrammedChange && !readOnly && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Programmed</span>
              <button
                type="button"
                onClick={() => onProgrammedChange(!programmedValue)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  border: 'none',
                  background: programmedValue ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : '#e5e7eb',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s'
                }}
                title={programmedValue ? 'Recipients will be set programmatically' : 'Enter recipients manually'}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: 3,
                  left: programmedValue ? 23 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          )}
        </div>
        {programmedValue && !allowTestInput ? (
          <div style={{ 
            padding: 16, 
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
            borderRadius: 12, 
            border: '1px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span style={{ fontSize: 16 }}>🤖</span>
            <span style={{ fontSize: 14, color: '#92400e', fontWeight: 500 }}>
              Recipients will be set programmatically when sending
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, background: '#f3f6fa', border: '1.5px solid #e3e8f0', borderRadius: 12, padding: '8px 12px', minHeight: 48, boxShadow: '0 2px 8px rgba(60,72,100,0.04)' }}>
            {value.map((email, idx) => (
              <span key={idx} style={{ display: 'flex', alignItems: 'center', background: '#e0e7ff', color: '#3730a3', borderRadius: 16, padding: '6px 14px', fontSize: 14, fontWeight: 500, boxShadow: '0 1px 4px rgba(60,72,100,0.07)', marginBottom: 2 }}>
                {email}
                {!readOnly && (
                  <button type="button" style={{ marginLeft: 8, color: '#a5b4fc', background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }} onClick={() => removeChip(idx)} aria-label="Remove">❌</button>
                )}
              </span>
            ))}
            {!readOnly && (
              <input
                type="email"
                style={{ flex: 1, minWidth: 120, background: 'transparent', border: 'none', outline: 'none', fontSize: 15, padding: '6px 0' }}
                placeholder={placeholder}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                    e.preventDefault();
                    addChip();
                  }
                }}
                onBlur={addChip}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Render block editor
  const renderBlockEditor = (block, idx) => {
    if (readOnly) {
      // Read-only mode - just display the content
      if (block.type === 'richtext') {
        return (
          <div style={{ padding: '12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <div dangerouslySetInnerHTML={{ __html: block.value || '' }} />
          </div>
        );
      } else if (block.type === 'button') {
        return (
          <div style={{ padding: '12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Button Text:</strong> {block.value.text || ''}
            </div>
            <div>
              <strong>Button Link:</strong> {block.value.link || ''}
            </div>
          </div>
        );
      } else if (block.type === 'image') {
        return (
          <div style={{ padding: '12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Image Source:</strong> {block.value.src || ''}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Width:</strong> {block.value.width || 'auto'}px
            </div>
            <div>
              <strong>Height:</strong> {block.value.height || 'auto'}px
            </div>
          </div>
        );
      }
      return null;
    }
    if (block.type === 'richtext') {
      return (
        <div>
          <ReactQuill
            ref={el => (quillRefs.current[`block-${idx}-richtext`] = el)}
            theme="snow"
            value={block.value || ''}
            onChange={val => updateBlock(idx, { ...block, value: val })}
            className="bg-white"
            onFocus={() => setFocusTarget({ type: 'block', index: idx })}
          />
        </div>
      );
    } else if (block.type === 'button') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="text"
            ref={el => (inputRefs.current[`block-${idx}-button-text`] = el)}
            style={{ padding: '8px 12px', border: '1.5px solid #e3e8f0', borderRadius: 8, fontSize: 15, marginBottom: 4 }}
            placeholder="Button Text"
            value={block.value.text || ''}
            onChange={e => updateBlock(idx, { ...block, value: { ...block.value, text: e.target.value } })}
            onFocus={() => setFocusTarget({ type: 'block', index: idx, subfield: 'text' })}
          />
          <input
            type="text"
            ref={el => (inputRefs.current[`block-${idx}-button-link`] = el)}
            style={{ padding: '8px 12px', border: '1.5px solid #e3e8f0', borderRadius: 8, fontSize: 15 }}
            placeholder="Button Link (URL)"
            value={block.value.link || ''}
            onChange={e => updateBlock(idx, { ...block, value: { ...block.value, link: e.target.value } })}
            onFocus={() => setFocusTarget({ type: 'block', index: idx, subfield: 'link' })}
          />
        </div>
      );
    } else if (block.type === 'image') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="text"
            ref={el => (inputRefs.current[`block-${idx}-image-src`] = el)}
            style={{ padding: '8px 12px', border: '1.5px solid #e3e8f0', borderRadius: 8, fontSize: 15, marginBottom: 4 }}
            placeholder="Image Source (URL)"
            value={block.value.src || ''}
            onChange={e => updateBlock(idx, { ...block, value: { ...block.value, src: e.target.value } })}
            onFocus={() => setFocusTarget({ type: 'block', index: idx, subfield: 'src' })}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              ref={el => (inputRefs.current[`block-${idx}-image-width`] = el)}
              style={{ padding: '8px 12px', border: '1.5px solid #e3e8f0', borderRadius: 8, fontSize: 15, width: 100 }}
              placeholder="Width (px)"
              value={block.value.width || ''}
              onChange={e => updateBlock(idx, { ...block, value: { ...block.value, width: e.target.value } })}
              onFocus={() => setFocusTarget({ type: 'block', index: idx, subfield: 'width' })}
            />
            <input
              type="number"
              ref={el => (inputRefs.current[`block-${idx}-image-height`] = el)}
              style={{ padding: '8px 12px', border: '1.5px solid #e3e8f0', borderRadius: 8, fontSize: 15, width: 100 }}
              placeholder="Height (px)"
              value={block.value.height || ''}
              onChange={e => updateBlock(idx, { ...block, value: { ...block.value, height: e.target.value } })}
              onFocus={() => setFocusTarget({ type: 'block', index: idx, subfield: 'height' })}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <EmailChipsInput
        label="To"
        value={emailFields.to}
        onChange={to => onEmailFieldsChange(prev => ({ ...prev, to }))}
        placeholder="Add recipient..."
        icon="👤"
        helper="Add one or more recipient email addresses"
        fieldName="to"
        programmedValue={emailFields.toProgrammed}
        onProgrammedChange={toProgrammed => onEmailFieldsChange(prev => ({ ...prev, toProgrammed }))}
        readOnly={readOnly}
        allowTestInput={allowTestInput}
      />
      <EmailChipsInput
        label="CC"
        value={emailFields.cc}
        onChange={cc => onEmailFieldsChange(prev => ({ ...prev, cc }))}
        placeholder="Add CC..."
        icon="👥"
        helper="Add CC recipients (optional)"
        fieldName="cc"
        programmedValue={emailFields.ccProgrammed}
        onProgrammedChange={ccProgrammed => onEmailFieldsChange(prev => ({ ...prev, ccProgrammed }))}
        readOnly={readOnly}
        allowTestInput={allowTestInput}
      />
      <EmailChipsInput
        label="BCC"
        value={emailFields.bcc}
        onChange={bcc => onEmailFieldsChange(prev => ({ ...prev, bcc }))}
        placeholder="Add BCC..."
        icon="🙈"
        helper="Add BCC recipients (optional)"
        fieldName="bcc"
        programmedValue={emailFields.bccProgrammed}
        onProgrammedChange={bccProgrammed => onEmailFieldsChange(prev => ({ ...prev, bccProgrammed }))}
        readOnly={readOnly}
        allowTestInput={allowTestInput}
      />
      {/* Subject */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <input
          type="text"
          ref={el => (inputRefs.current.subject = el)}
          value={emailFields.subject}
          onChange={e => onEmailFieldsChange(prev => ({ ...prev, subject: e.target.value }))}
          style={{ width: '100%', padding: '18px 16px 8px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 17, background: readOnly ? '#f9fafb' : '#fff', fontWeight: 500, outline: 'none', transition: 'border-color 0.2s' }}
          placeholder="Subject line"
          required
          disabled={readOnly}
          onFocus={() => !readOnly && setFocusTarget({ type: 'subject' })}
        />
        <label style={{ position: 'absolute', left: 18, top: 8, color: '#2563eb', fontSize: 14, fontWeight: 600, background: '#fff', padding: '0 4px' }}>Subject</label>
      </div>
      {/* Preview Text */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <input
          type="text"
          ref={el => (inputRefs.current.previewText = el)}
          value={emailFields.previewText}
          onChange={e => onEmailFieldsChange(prev => ({ ...prev, previewText: e.target.value }))}
          style={{ width: '100%', padding: '18px 16px 8px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 17, background: readOnly ? '#f9fafb' : '#fff', fontWeight: 500, outline: 'none', transition: 'border-color 0.2s' }}
          placeholder="Preview text (shows in inbox preview)"
          disabled={readOnly}
          onFocus={() => !readOnly && setFocusTarget({ type: 'previewText' })}
        />
        <label style={{ position: 'absolute', left: 18, top: 8, color: '#2563eb', fontSize: 14, fontWeight: 600, background: '#fff', padding: '0 4px' }}>Preview Text</label>
      </div>
      {/* Body (Content Blocks) */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#374151', marginBottom: 12 }}>Body (Content Blocks)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {bodyBlocks.map((block, idx) => (
            <div key={idx} style={{ borderRadius: 18, boxShadow: '0 4px 16px rgba(37,99,235,0.07)', padding: 24, background: block.type === 'richtext' ? 'linear-gradient(135deg, #fff 0%, #e0e7ff 100%)' : block.type === 'button' ? 'linear-gradient(135deg, #fff 0%, #bbf7d0 100%)' : 'linear-gradient(135deg, #fff 0%, #fef9c3 100%)', borderLeft: `8px solid ${block.type === 'richtext' ? '#2563eb' : block.type === 'button' ? '#10b981' : '#f59e0b'}`, position: 'relative', marginBottom: 0 }}>
                {!readOnly && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 28, marginRight: 6 }}>
                      {block.type === 'richtext' && '✏️'}
                      {block.type === 'button' && '🔗'}
                      {block.type === 'image' && '🖼️'}
                    </span>
                    <select
                      value={block.type}
                      onChange={e => updateBlock(idx, { ...block, type: e.target.value, value: e.target.value === 'richtext' ? '' : {}, align: block.align || 'left' })}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e3e8f0', fontWeight: 600, fontSize: 15, background: '#fff', marginRight: 8 }}
                    >
                      {BLOCK_TYPES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 13, color: '#888', marginRight: 8 }}>Type</span>
                    <select
                      value={block.align || 'left'}
                      onChange={e => updateBlock(idx, { ...block, align: e.target.value })}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e3e8f0', fontWeight: 600, fontSize: 15, background: '#fff', marginRight: 8 }}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                    <span style={{ fontSize: 13, color: '#888' }}>Align</span>
                    <button type="button" onClick={() => moveBlock(idx, -1)} disabled={idx === 0} style={{ color: idx === 0 ? '#ccc' : '#2563eb', background: 'none', border: 'none', fontSize: 20, marginLeft: 8, cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>⬆️</button>
                    <button type="button" onClick={() => moveBlock(idx, 1)} disabled={idx === bodyBlocks.length - 1} style={{ color: idx === bodyBlocks.length - 1 ? '#ccc' : '#2563eb', background: 'none', border: 'none', fontSize: 20, marginLeft: 2, cursor: idx === bodyBlocks.length - 1 ? 'not-allowed' : 'pointer' }}>⬇️</button>
                    <button type="button" onClick={() => removeBlock(idx)} style={{ color: '#d32f2f', background: 'none', border: 'none', fontSize: 22, marginLeft: 'auto', cursor: 'pointer' }}>🗑️</button>
                  </div>
                )}
                {renderBlockEditor(block, idx)}
              </div>
          ))}
          {/* Floating Add Block Button */}
          {!readOnly && (
            <button type="button" onClick={() => addBlock()} style={{
              position: 'fixed',
              bottom: 40,
              right: 40,
              zIndex: 50,
              background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
              color: 'white',
              borderRadius: '50%',
              boxShadow: '0 8px 32px rgba(37,99,235,0.18)',
              padding: 0,
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              animation: 'bounce 1.5s infinite',
            }}
              onMouseEnter={e => {
                e.target.style.transform = 'scale(1.08)';
                e.target.style.boxShadow = '0 12px 40px rgba(37,99,235,0.25)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(37,99,235,0.18)';
              }}
            >
              ➕
            </button>
          )}
          {/* Floating Attribute Inserter Button */}
          {showAttributeInserter && !readOnly && (
            <button type="button" onClick={openAttrDialog} style={{
              position: 'fixed',
              bottom: 40,
              left: 40,
              zIndex: 50,
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
              color: 'white',
              borderRadius: '50%',
              boxShadow: '0 8px 32px rgba(245,158,11,0.18)',
              padding: 0,
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              animation: 'bounce 1.5s infinite',
            }}
              onMouseEnter={e => {
                e.target.style.transform = 'scale(1.08)';
                e.target.style.boxShadow = '0 12px 40px rgba(245,158,11,0.25)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(245,158,11,0.18)';
              }}
              title="Insert Attribute"
            >
              🏷️
            </button>
          )}
        </div>
      </div>
      {/* Live Preview */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#374151', marginBottom: 12 }}>Live Preview</div>
        <div style={{ borderRadius: 18, padding: 24, background: 'linear-gradient(135deg, #fff 0%, #e0e7ff 100%)', boxShadow: '0 2px 8px rgba(60,72,100,0.07)', minHeight: 160, position: 'relative', overflowX: 'auto' }}>
          <div style={{ maxWidth: 600, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(60,72,100,0.07)', padding: 18, border: '1px solid #e3e8f0', minHeight: 120 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>(Preview as it will appear in most email clients)</div>
            <div dangerouslySetInnerHTML={{ __html: buildBodyHtml() }} />
          </div>
        </div>
      </div>

      {/* Custom content (like submit buttons) */}
      {children}

      {/* Attribute Inserter Dialog */}
      {showAttrDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.85)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 36,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            textAlign: 'center',
            minWidth: 320,
            border: '1.5px solid #fbbf24',
            maxWidth: 400
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b', marginBottom: 12 }}>🏷️ Insert Attribute</div>
            <div style={{ fontSize: 16, color: '#333', marginBottom: 18, fontWeight: 500 }}>
              Enter the attribute name to insert as <span style={{ color: '#f59e0b', fontWeight: 700 }}>{'${attribute}'}</span>
            </div>
            <input
              type="text"
              value={attrName}
              onChange={e => setAttrName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              placeholder="attribute_name"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #fbbf24',
                borderRadius: 8,
                fontSize: 18,
                outline: 'none',
                marginBottom: 24,
                fontWeight: 600,
                background: '#fffbe8',
                color: '#b45309',
                letterSpacing: 1
              }}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleAttrInsert();
                if (e.key === 'Escape') closeAttrDialog();
              }}
            />
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button
                onClick={handleAttrInsert}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 32px',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: attrName ? 'pointer' : 'not-allowed',
                  opacity: attrName ? 1 : 0.6
                }}
                disabled={!attrName}
              >
                OK
              </button>
              <button
                onClick={closeAttrDialog}
                style={{
                  background: '#f3f4f6',
                  color: '#b45309',
                  border: '1.5px solid #fbbf24',
                  borderRadius: 8,
                  padding: '12px 32px',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailComposer; 